import { z } from 'zod';
import { numberString, requiredNumberString, validateMobile } from '../helper';
import { VerificationType } from '../types';

export enum CodeAcceptanceStatus {
  NotAccepted = 'NotAccepted',
  Sms = 'SmsCodeAccepted',
  Email = 'EmailCodeAccepted',
}

const checkOtpCodeLength = z
  .string()
  .optional()
  .refine(val => !val || val.length === 6, {
    message: `Enter a 6-digit code`,
  });

export const baseContactDetailsSchema = z.object({
  email: z.string().trim().email().optional(),
  mobile: validateMobile,
  verificationType: z.nativeEnum(VerificationType).optional(),
  otpSent: z.boolean().default(false),
  codeAccepted: z.nativeEnum(CodeAcceptanceStatus).default(CodeAcceptanceStatus.NotAccepted),
  updateOtpSent: z.boolean().default(false),
});

const otpSentSchema = z.object({
  otpSent: z.literal(true),
  otp: checkOtpCodeLength.and(requiredNumberString()),
});

const otpNotSentSchema = z.object({
  otpSent: z.literal(false),
  otp: checkOtpCodeLength.and(numberString()),
});

const verificationTypeSms = z.object({
  verificationType: z.literal(VerificationType.Sms),
  email: z.string().trim().email().optional(),
  mobile: validateMobile,
});

const verificationTypeEmail = z.object({
  verificationType: z.literal(VerificationType.Email),
  mobile: validateMobile.optional(),
  email: z.string().trim().email(),
});

const verificationTypeSmsEmail = z.object({
  verificationType: z.literal(VerificationType.SmsEmail),
  email: z.string().trim().email(),
  mobile: z.string().min(1),
});

// Universal login versions where email is optional
const verificationTypeEmailUniversal = z.object({
  verificationType: z.literal(VerificationType.Email),
  mobile: validateMobile.optional(),
  email: z.string().trim().email().optional(),
});

const verificationTypeSmsEmailUniversal = z.object({
  verificationType: z.literal(VerificationType.SmsEmail),
  email: z.string().trim().email().optional(),
  mobile: z.string().min(1),
});

const codeIsAcceptedSms = z.object({
  codeAccepted: z.literal(CodeAcceptanceStatus.Sms),
  newEmail: z.string().trim().email(),
});

const codeIsAcceptedEmail = z.object({
  codeAccepted: z.literal(CodeAcceptanceStatus.Email),
  newMobile: validateMobile,
});

const codeNotAccepted = z.object({
  codeAccepted: z.literal(CodeAcceptanceStatus.NotAccepted),
  newEmail: z.string().trim().email().optional(),
  newMobile: validateMobile.optional(),
});

const updateOtpSent = z.object({
  updateOtpSent: z.literal(true),
  updateOtp: checkOtpCodeLength.and(requiredNumberString()),
});

const updateOtpNotSent = z.object({
  updateOtpSent: z.literal(false),
  updateOtp: checkOtpCodeLength.and(numberString()),
});
export const contactDetailsSchema = baseContactDetailsSchema
  .and(z.discriminatedUnion('otpSent', [otpNotSentSchema, otpSentSchema]))
  .and(z.discriminatedUnion('verificationType', [verificationTypeSms, verificationTypeEmail, verificationTypeSmsEmail]))
  .and(z.discriminatedUnion('codeAccepted', [codeIsAcceptedSms, codeIsAcceptedEmail, codeNotAccepted]))
  .and(z.discriminatedUnion('updateOtpSent', [updateOtpSent, updateOtpNotSent]));

export const contactDetailsSchemaUniversal = baseContactDetailsSchema
  .and(z.discriminatedUnion('otpSent', [otpNotSentSchema, otpSentSchema]))
  .and(
    z.discriminatedUnion('verificationType', [
      verificationTypeSms,
      verificationTypeEmailUniversal,
      verificationTypeSmsEmailUniversal,
    ]),
  )
  .and(z.discriminatedUnion('codeAccepted', [codeIsAcceptedSms, codeIsAcceptedEmail, codeNotAccepted]))
  .and(z.discriminatedUnion('updateOtpSent', [updateOtpSent, updateOtpNotSent]));

export const getContactDetailsSchema = (isUniversalLogin: boolean = false) =>
  isUniversalLogin ? contactDetailsSchemaUniversal : contactDetailsSchema;

export type ContactDetailsType = z.infer<typeof contactDetailsSchema> & {
  codeAccepted?: CodeAcceptanceStatus;
  newEmail?: string;
  newMobile?: string;
  maskedEmail?: string;
  maskedMobile?: string;
  financialProductId?: string;
};
